import React, { Fragment } from 'react';
import { Query } from '@apollo/client/react/components';
import { sortBy } from 'lodash';
import { Text } from 'bench-internal-theme';
import styled from 'styled-components';

import ReviewSection from './ReviewSection';
import { LedgerQuery, ReviewChecksQuery } from './queries';
import DownloadGeneralLedger from './DownloadGeneralLedger';
import { getISODateString, toDate } from '../date-functions';

const TransactionDescriptionText = styled(Text)`
  margin-bottom: 16px;
  line-height: 21px;
`;

type transactionChecksDescriptionProps = {
  clientId: string;
  description: string;
  startYearMonth: string;
  endYearMonth: string;
};

/**
 * Description for Transaction Checks Section that adds general ledger download.
 */
const TransactionChecksDescription = ({
  clientId,
  description,
  startYearMonth,
  endYearMonth,
}: transactionChecksDescriptionProps) => (
  <Fragment>
    <TransactionDescriptionText>{description}</TransactionDescriptionText>
    <DownloadGeneralLedger
      clientId={clientId}
      startYearMonth={startYearMonth}
      endYearMonth={endYearMonth}
    />
  </Fragment>
);

type ReviewSectionContainerProps = {
  id: string;
  name: string;
  description: string;
  startDate: string;
  endDate: string;
  restoreLastChecks: boolean;
  clientId: string;
  onCheckStatusListUpdate: (checkStatusList: Record<string, unknown>[]) => void;
  onSidebarToggle: (data: { journalEntryId?: string; ledgerId?: string }) => void;
};

const transactionChecksSection = '5';

// This mapping is a duplicate of what the server provides.
const categoryIds = {
  ASSETS: 1,
  LIABILITIES: 3,
  EQUITY: 5,
  REVENUES: 6,
  OPERATING_EXPENSES: 7,
  COST_OF_SALES: 8,
};

const categoryIdSortMapping = {
  [categoryIds.ASSETS]: 1,
  [categoryIds.LIABILITIES]: 2,
  [categoryIds.EQUITY]: 3,
  [categoryIds.REVENUES]: 4,
  [categoryIds.COST_OF_SALES]: 5,
  [categoryIds.OPERATING_EXPENSES]: 6,
};

const findLedgerOrder = (ledgerAccounts, id) => {
  const ledgerAccount = ledgerAccounts.find(ledger => id === ledger.id);

  if (ledgerAccount) {
    return categoryIdSortMapping[ledgerAccount.categoryId];
  }

  return null; // Push unknowns down to the bottom.
};

const sortChecksByLedgerCategory = (ledgerAccounts, checks) =>
  sortBy(checks, check => findLedgerOrder(ledgerAccounts, check.ledgerId));

const getTwoMonthPrior = yearMonth => {
  const providedDate = toDate(yearMonth);
  const yearMonthEndIndex = 7;

  providedDate.setMonth(providedDate.getMonth() - 2);

  return getISODateString(providedDate).substr(0, yearMonthEndIndex);
};

const ReviewSectionContainer = ({
  id,
  name,
  description,
  startDate,
  endDate,
  restoreLastChecks,
  clientId,
  onCheckStatusListUpdate,
  onSidebarToggle,
}: ReviewSectionContainerProps) => (
  <Query
    query={ReviewChecksQuery}
    fetchPolicy="network-only" // Apollo will normalize data so we have to enforce 'network-only' to show correct default values.
    variables={{
      sectionId: id,
      clientId,
      startDate,
      endDate,
    }}
  >
    {({ loading, error, data: reviewChecksData }: any) => {
      const reviewChecks = reviewChecksData?.reviewChecks;

      // Section 5 needs to sort the list of checks by categoryId of the associated ledger.
      // To minimize how invasive this case is we isolate the data manipulation here.
      if (id === transactionChecksSection) {
        return (
          <Query query={LedgerQuery} variables={{ clientId }}>
            {({
              loading: loadingLedgerAccounts,
              error: errorLedgerAccounts,
              data: ledgerAccountsData,
            }: any) => {
              const ledgerAccounts = ledgerAccountsData?.ledgerAccounts;
              const checks = sortChecksByLedgerCategory(ledgerAccounts || [], reviewChecks || []);
              const startYearMonthGL = getTwoMonthPrior(startDate);
              return (
                <ReviewSection
                  name={name}
                  description={
                    <TransactionChecksDescription
                      clientId={clientId}
                      description={description}
                      startYearMonth={startYearMonthGL}
                      endYearMonth={endDate}
                    />
                  }
                  clientId={clientId}
                  startDate={startDate}
                  endDate={endDate}
                  restoreLastChecks={restoreLastChecks}
                  onCheckStatusListUpdate={onCheckStatusListUpdate}
                  reviewChecks={checks}
                  loading={loading || loadingLedgerAccounts}
                  error={error || errorLedgerAccounts}
                  onSidebarToggle={onSidebarToggle}
                />
              );
            }}
          </Query>
        );
      }

      return (
        <ReviewSection
          name={name}
          description={description}
          clientId={clientId}
          startDate={startDate}
          endDate={endDate}
          restoreLastChecks={restoreLastChecks}
          onCheckStatusListUpdate={onCheckStatusListUpdate}
          reviewChecks={reviewChecks || []}
          startExpanded={id === '6'}
          loading={loading}
          error={error}
          onSidebarToggle={onSidebarToggle}
        />
      );
    }}
  </Query>
);

export default ReviewSectionContainer;
